import snakeCase from 'lodash/snakeCase';
import {useCallback, useContext, useEffect, useState} from 'react';
import {FabricInvitationContext} from '~/common/FabricInvitation/context/FabricInvitationContext';
import {useUyoValidation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useUyoValidation';
import type {FabricInvitationValidationResponse} from '~/common/FabricInvitation/types';
import {ValidationModal} from '~/OneStepCreateInvitation/components/ValidationModal/ValidationModal';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';

const scrollToError = (error: FabricInvitationValidationResponse) => {
  const scrollDiv = document.querySelector('.scrollable');
  if (error.ref && error.ref.style.display !== 'none') {
    if (error.ref.tagName.toLowerCase() === 'button') {
      setTimeout(() => error.ref?.click(), 50);
    } else if (['input', 'textarea'].includes(error.ref.tagName.toLowerCase())) {
      setTimeout(() => error.ref?.focus(), 50);
    } else {
      error.ref.scrollIntoView({block: 'center', behavior: 'smooth'});
    }
  } else {
    const top = error.top || 100;
    scrollDiv?.scroll({top: top - 100, behavior: 'smooth'});
  }
};

const buildMessage = (
  errors: FabricInvitationValidationResponse[],
  onClose: (errorToTrack: FabricInvitationValidationResponse) => void
) => {
  const errorMessages = errors
    .sort((a, b) => a.order - b.order)
    .map((error, index) => {
      const errorMessage = error.getMessage(index);

      const onClick = () => onClose(error);

      const dataQaId = snakeCase(errorMessage);

      return (
        <li key={errorMessage}>
          <Button onClick={onClick} data-qa-id={dataQaId} variant="transparent">
            <Typography variant="list2" color="link" size="large">
              {errorMessage}
            </Typography>
          </Button>
        </li>
      );
    });

  return (
    <div className="onestep-invitation-validation-modal__error">
      <Typography variant="paragraph2">
        To finish your invitation, please complete the following items:
      </Typography>
      <ul>{errorMessages}</ul>
    </div>
  );
};

export const useValidation = () => {
  const [error, setError] = useState<{
    errors: FabricInvitationValidationResponse[];
    error: boolean;
    title: string;
    message: React.ReactNode;
  } | null>(null);
  const [selectedError, setSelectedError] = useState<FabricInvitationValidationResponse | null>(
    null
  );
  const {formValidations} = useContext(FabricInvitationContext);
  const {validate: uyoValidation} = useUyoValidation();

  // Close the modal, and if an error message was clicked, track it so we can handle it after the modal is dismissed
  const close = useCallback((errorToTrack?: FabricInvitationValidationResponse) => {
    setSelectedError(errorToTrack || null);
    setError(null);
  }, []);

  // If an error was selected from the modal, scroll to it and trigger its callback
  useEffect(() => {
    if (error || !selectedError) return;
    scrollToError(selectedError);
    selectedError.callback?.();
  }, [error, selectedError]);

  const goToFirstError = useCallback(() => {
    const firstError = error?.errors.sort((a, b) => a.order - b.order).at(0);
    setSelectedError(firstError || null);
    setError(null);
  }, [error]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const validate = useCallback(() => {
    const errors = Object.values(formValidations.current)
      .map((fn) => fn())
      .filter(Boolean) as FabricInvitationValidationResponse[];

    if (errors.length) {
      setError({
        errors,
        error: true,
        title: 'Missing Details',
        message: buildMessage(errors, close),
      });
    }

    return errors.length;
  }, [uyoValidation, setError]);

  const ErrorModal = (
    <ValidationModal error={error} goToFirstError={goToFirstError} onClose={() => close()} />
  );

  return {validate, error, ErrorModal};
};
